import { Layout, message } from "antd";
import Sidebar from "components/Sidebar";
import SpaceObjects from "./SpaceObjects";
import { useMsal } from "@azure/msal-react";
import { IDomainsConfig } from "pages/Ponds/types";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Content, Footer, Header } from "antd/lib/layout/layout";
import { NormalizedSpace, normalizeSpaces } from "utils/normalizeSpaces";
import { fetchDomainsConfig, fetchListBucketsAdvanced } from "pages/Explorer/explorerApi";
import { InteractionStatus } from "@azure/msal-browser";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Query key factory for space details
const spaceDetailsQueryKeys = {
  all: ["space-details"] as const,
  domain: (domain: string) => [...spaceDetailsQueryKeys.all, domain] as const,
  space: (domain: string, spaceName: string) => [...spaceDetailsQueryKeys.domain(domain), spaceName] as const,
};

export default function SpaceDetailView() {
    const [collapsed, setCollapsed] = useState(true);
    const [selectedDomain, setSelectedDomain] = useState<IDomainsConfig | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const msalInstance = useMsal();
    const queryClient = useQueryClient();
    const { domain, spaceName } = useParams<{ domain: string, spaceName: string }>();

    const accountsReady = msalInstance.accounts.length > 0;
    const authReady = accountsReady && msalInstance.inProgress === InteractionStatus.None;

    // Check for unsubscribe success query parameter
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const unsubscribed = searchParams.get("unsubscribed");
        const spaceNameParam = searchParams.get("space_name");

        if (unsubscribed === "true") {
            // Show success message
            message.success({
                content: spaceNameParam 
                    ? `Successfully unsubscribed from ${spaceNameParam}` 
                    : "Successfully unsubscribed from this space",
                duration: 3,
            });

            // Clean up URL - remove query parameters
            const newUrl = `${location.pathname}`;
            navigate(newUrl, { replace: true });
        } else if (unsubscribed === "false") {
            // Handle error case
            const error = searchParams.get("error") || "Failed to unsubscribe. Please try again.";
            message.error({
                content: error,
                duration: 3,
            });

            // Clean up URL
            const newUrl = `${location.pathname}`;
            navigate(newUrl, { replace: true });
        }
    }, [location.search, location.pathname, navigate]);

    // React Query for fetching space details
    const {
        data: spacesResponse,
        isLoading: isSpaceListFetching,
        error: spacesError,
        refetch: refetchSpaces,
    } = useQuery({
        queryKey: spaceDetailsQueryKeys.space(domain || "", spaceName || ""),
        queryFn: async () => {
            if (!selectedDomain || !spaceName) {
                throw new Error("Domain or space name not available");
            }

            console.log("Fetching space details for:", { domain: selectedDomain.display_name, spaceName });

            const apiParams = {
                msalInstance,
                region: "all", // You can make this dynamic if needed
                domain: selectedDomain.display_name,
                scope: "all" as const,
                searchSpaceName: spaceName, // Pass the spaceName to filter for specific space
                page: 1,
                pageSize: 1, // Only need one space
            };

            const response = await fetchListBucketsAdvanced(apiParams);
            console.log("Space details API response:", response);
            return response;
        },
        enabled: !!selectedDomain && !!spaceName && authReady,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    });

    // Get normalized spaces from the response
    const spaces: NormalizedSpace[] = useMemo(() => {
        if (spacesResponse?.spaces) {
            return normalizeSpaces(spacesResponse.spaces);
        }
        return [];
    }, [spacesResponse]);

    // When a domain is selected in the sidebar
    const handleDomainSelect = (domainConfig: IDomainsConfig) => {
        setSelectedDomain(domainConfig);
        navigate(`/explorer/${domainConfig.display_name}`);
        
        // Invalidate queries when domain changes
        queryClient.invalidateQueries({
            queryKey: spaceDetailsQueryKeys.all,
        });
    };

    // Handle refresh for SpaceObjects component
    const handleRefreshSpaces = () => {
        console.log("Refreshing space details...");
        refetchSpaces();
    };

    // Initialize domain data
    useEffect(() => {
        if (!domain || !authReady) return;

        const initDomainAndSpaces = async () => {
            let domains: IDomainsConfig[] = [];

            const cached = localStorage.getItem("domainsData");
            if (cached) {
                try {
                    domains = JSON.parse(cached);
                } catch {
                    domains = [];
                }
            }

            if (!Array.isArray(domains) || domains.length === 0) {
                console.log("Fetching domains from API...");
                domains = await fetchDomainsConfig(msalInstance);
                localStorage.setItem("domainsData", JSON.stringify(domains));
            }

            const found = domains.find((d) => d.display_name === domain);
            if (!found) {
                console.warn(`Domain ${domain} not found`);
                return;
            }

            console.log("Setting selected domain:", found.display_name);
            setSelectedDomain(found);
            // React Query will automatically fetch space details when selectedDomain is set
        };

        initDomainAndSpaces();
    }, [domain, authReady, msalInstance]);

    // Handle errors
    useEffect(() => {
        if (spacesError) {
            console.error("Error fetching space details:", spacesError);
        }
    }, [spacesError]);

    // sidebar sizes
    const collapsedWidth = 50;
    const fullWidth = 300;

    // Find the correct NormalizedSpace object by spaceName
    const selectedSpace: NormalizedSpace | undefined = useMemo((): NormalizedSpace | undefined => {
        return spaces.find((space: NormalizedSpace) => space.spaceName === spaceName);
    }, [spaces, spaceName]);

    // Handle delete access (placeholder - implement as needed)
    const handleDeleteAccess = (metaDataUsers: string[]) => {
        console.log("Delete access requested for users:", metaDataUsers);
        // Implement delete access logic here
        // You might want to call an API and then refresh the data
        // refetchSpaces();
    };

    return (
        <Layout style={{ marginLeft: collapsed ? collapsedWidth : fullWidth }}>
            <Sidebar
                collapsed={collapsed}
                collapsedWidth={collapsedWidth}
                width={fullWidth}
                selectedDomainKey={selectedDomain?.display_name || domain}
                onCollapse={setCollapsed}
                onDomainSelect={handleDomainSelect}
            />
            <Layout>
                <Header style={{ padding: 0 }} />
                <Content style={{ margin: "0 16px" }}>
                    <div style={{ padding: 24, paddingTop: 24, paddingBottom: 0, textAlign: "right" }}>
                        {domain && spaceName && authReady && (
                            <SpaceObjects
                                domainName={domain}
                                spaces={selectedSpace ? [selectedSpace] : []}
                                isSpaceListFetching={isSpaceListFetching}
                                onRefreshSpaces={handleRefreshSpaces}
                                onDeleteAccess={handleDeleteAccess}
                            />
                        )}
                    </div>
                </Content>
                <Footer style={{ textAlign: "center" }} />
            </Layout>
        </Layout>
    );
}